"use client";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useMemo,
  useCallback
} from "react";
import { League, ApiResponse } from "@/types/sports";
import { sportService } from "@/lib/services/sportService";
import { useSport } from "./SportContext";

interface LeagueContextType {
  activeLeague: League | null;
  switchLeague: (leagueId: string) => void;
  availableLeagues: League[];
  isLoadingLeagues: boolean;
  error: string | null;
  clearError: () => void;
}

// Helper function to normalize API responses with potential nested data structures
function normalizeApiResponse<T>(response: ApiResponse<unknown>): T[] {
  if (!response.success) return [];
  
  if (Array.isArray(response.data)) {
    return response.data as T[];
  } 
  
  if (response.data && typeof response.data === 'object') {
    const dataObj = response.data as {data?: T[]};
    if (dataObj.data && Array.isArray(dataObj.data)) {
      return dataObj.data;
    }
  }
  
  return [];
}

const LeagueContext = createContext<LeagueContextType | undefined>(undefined);

export function LeagueProvider({ children }: { children: ReactNode }) {
  const { activeSport } = useSport();
  const [activeLeague, setActiveLeague] = useState<League | null>(null);
  const [availableLeagues, setAvailableLeagues] = useState<League[]>([]);
  const [isLoadingLeagues, setIsLoadingLeagues] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear error helper function
  const clearError = useCallback(() => setError(null), []);

  // Fetch leagues when active sport changes
  useEffect(() => {
    if (!activeSport) return;

    let isMounted = true;
    setIsLoadingLeagues(true);
    setError(null);
    
    const fetchLeagues = async () => {
      try {
        const response = await sportService.getLeaguesBySport(activeSport);
        if (!isMounted) return;
        
        if (response.success) {
          // Use the normalize helper to handle response structure
          const leaguesData = normalizeApiResponse<League>(response);
          setAvailableLeagues(leaguesData);
          
          // Auto-select the first league if available
          if (leaguesData.length > 0) {
            setActiveLeague(leaguesData[0]);
          } else {
            // Clear active league when there are no leagues available
            setActiveLeague(null);
          }
        } else {
          setError(response.error || "Failed to fetch leagues");
        }
      } catch (err) {
        if (isMounted) {
          setError("An unexpected error occurred");
          console.error("Error fetching leagues:", err);
        }
      } finally {
        if (isMounted) {
          setIsLoadingLeagues(false);
        }
      }
    };

    fetchLeagues();
    
    return () => {
      isMounted = false;
    };
  }, [activeSport]); // Only depends on activeSport

  const switchLeague = useCallback((leagueId: string) => {
    const league = availableLeagues.find((l) => l.id === leagueId);
    if (league) {
      setActiveLeague(league);
    }
  }, [availableLeagues]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      activeLeague,
      switchLeague,
      availableLeagues,
      isLoadingLeagues,
      error,
      clearError
    }),
    [
      activeLeague,
      switchLeague,
      availableLeagues,
      isLoadingLeagues,
      error,
      clearError
    ]
  );

  return (
    <LeagueContext.Provider value={contextValue}>
      {children}
    </LeagueContext.Provider>
  );
}

export const useLeague = (): LeagueContextType => {
  const context = useContext(LeagueContext);
  if (context === undefined) {
    throw new Error("useLeague must be used within a LeagueProvider");
  }
  return context;
}; 